import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { captureColdLead } from '../lib/api';
import { Logo } from '../components/Logo';

const BENCHMARKS = [
  { emoji: '🛻', label: 'Used Hilux', cost: 35000 },
  { emoji: '🏖️', label: 'Family holidays', cost: 6000 },
  { emoji: '🛠️', label: 'Apprentice (1 yr)', cost: 55000 },
  { emoji: '🏠', label: 'Mortgage payments', cost: 3500 },
  { emoji: '🎣', label: 'Fishing weekends', cost: 800 },
  { emoji: '⚽', label: "Kids' sport seasons", cost: 600 },
];

function fmtCurrency(value) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function fmtNumber(value) {
  return Math.round(value || 0).toLocaleString('en-AU');
}

export default function MissedCallAuditPage() {
  const [calls, setCalls] = useState(5);
  const [ticket, setTicket] = useState(670);
  const [conversionPct, setConversionPct] = useState(50);
  const [contactMode, setContactMode] = useState('email');
  const [contactValue, setContactValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const metrics = useMemo(() => {
    const conversion = conversionPct / 100;
    const lostJobsPerYear = calls * 52 * conversion;
    const annual = lostJobsPerYear * ticket;
    const weekly = annual / 52;
    const monthly = annual / 12;
    return { annual, weekly, monthly, lostJobsPerYear };
  }, [calls, ticket, conversionPct]);

  const whatCouldBuy = useMemo(
    () =>
      BENCHMARKS.map((b) => {
        const count = metrics.annual / b.cost;
        const display = count >= 1 ? (count >= 10 ? Math.round(count) : count.toFixed(1)) : count.toFixed(2);
        return { ...b, display };
      }),
    [metrics.annual]
  );

  useEffect(() => {
    const canonical = `${window.location.origin}/missed-call-audit-87k`;
    document.title = 'Missed-Call Audit for Tradies | Desqly';
    const upsert = (key, value, content) => {
      let el = document.head.querySelector(`meta[${key}="${value}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(key, value);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    upsert(
      'name',
      'description',
      'Free 60-second missed-call audit for tradies. Estimate annual lost revenue and request a tailored recovery plan.'
    );
    upsert('property', 'og:title', 'Free Missed-Call Audit | Desqly');
    upsert('property', 'og:description', 'See how much missed calls are costing your trade business each year.');
    upsert('property', 'og:url', canonical);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, []);

  const validate = () => {
    const v = contactValue.trim();
    if (contactMode === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    const digits = v.replace(/[^\d+]/g, '');
    return /^(\+?61|0)\d{8,10}$/.test(digits);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(contactMode === 'email' ? 'Please enter a valid email address.' : 'Please enter a valid phone number.');
      return;
    }
    setSubmitting(true);
    try {
      await captureColdLead({
        source: 'missed_call_audit',
        ...(contactMode === 'phone'
          ? { phone: contactValue.trim() }
          : { email: contactValue.trim() }),
        details: `Missed call audit request: ${calls} missed calls/week, ${ticket} avg job value AUD, ${conversionPct}% conversion.`,
        pricing_context: {
          audit_type: 'missed_call_revenue_leak',
          calls_per_week: calls,
          avg_job_value_aud: ticket,
          conversion_percent: conversionPct,
          annual_lost_revenue_aud: Math.round(metrics.annual),
          monthly_lost_revenue_aud: Math.round(metrics.monthly),
          weekly_lost_revenue_aud: Math.round(metrics.weekly),
          estimated_jobs_lost_per_year: Number(metrics.lostJobsPerYear.toFixed(1)),
        },
      });
      setSubmitted(true);
      toast.success(contactMode === 'email' ? 'Audit details sent to your inbox.' : "Thanks — we'll call you back shortly.");
    } catch {
      toast.error('Could not submit right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 via-white to-white px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Logo size="default" linkTo="/" />
          <Button asChild className="bg-teal-600 hover:bg-teal-700">
            <Link to="/?lead=1">Get Started</Link>
          </Button>
        </div>

        <div className="text-center max-w-3xl mx-auto mb-10">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4">Free 60-second audit</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
            How much are missed calls costing you every year?
          </h1>
          <p className="text-slate-600 text-lg">
            Move the sliders, see your number, and get a tailored plan to recover lost revenue.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-teal-600" />Your numbers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex justify-between mb-2"><Label>Missed calls per week</Label><span className="font-bold text-teal-700">{calls}</span></div>
                <input type="range" min={0} max={25} step={1} value={calls} onChange={(e) => setCalls(Number(e.target.value))} className="w-full" />
              </div>
              <div>
                <div className="flex justify-between mb-2"><Label>Average job value</Label><span className="font-bold text-teal-700">{fmtCurrency(ticket)}</span></div>
                <input type="range" min={100} max={5000} step={10} value={ticket} onChange={(e) => setTicket(Number(e.target.value))} className="w-full" />
              </div>
              <div>
                <div className="flex justify-between mb-2"><Label>Quote-to-job conversion</Label><span className="font-bold text-teal-700">{conversionPct}%</span></div>
                <input type="range" min={10} max={90} step={5} value={conversionPct} onChange={(e) => setConversionPct(Number(e.target.value))} className="w-full" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-teal-300 bg-gradient-to-br from-teal-50 to-white">
            <CardHeader>
              <CardDescription className="uppercase tracking-wider text-teal-700 font-semibold">Annual revenue leak</CardDescription>
              <CardTitle className="text-5xl sm:text-6xl font-black text-teal-700">{fmtCurrency(metrics.annual)}</CardTitle>
              <p className="text-slate-600">vanishing into voicemail every year</p>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-3 text-center border-t border-teal-100 pt-4">
              <div><div className="font-bold text-slate-900">{fmtCurrency(metrics.weekly)}</div><div className="text-xs text-slate-500">per week</div></div>
              <div><div className="font-bold text-slate-900">{fmtCurrency(metrics.monthly)}</div><div className="text-xs text-slate-500">per month</div></div>
              <div><div className="font-bold text-slate-900">{fmtNumber(metrics.lostJobsPerYear)}</div><div className="text-xs text-slate-500">jobs/year lost</div></div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-6">
          <CardHeader><CardTitle>What that money could buy</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whatCouldBuy.map((item) => (
              <div key={item.label} className="rounded-xl border border-slate-200 p-4 text-center">
                <div className="text-3xl mb-2">{item.emoji}</div>
                <p className="font-semibold text-slate-900">{item.label}</p>
                <p className="text-teal-700 text-sm font-bold">{item.display}× / year</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-0 text-white">
          <CardHeader>
            <CardDescription className="text-teal-300 uppercase tracking-wider font-semibold">Stop the leak</CardDescription>
            <CardTitle className="text-3xl">Want a custom audit + 15-min recovery plan?</CardTitle>
            <p className="text-slate-300">
              Leave your email or phone and we will send a tailored breakdown using your calculator results.
            </p>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="rounded-xl bg-teal-700/30 border border-teal-400 p-4">
                {contactMode === 'email'
                  ? 'Got it. Your audit details are on the way to your inbox.'
                  : "Got it. We'll call you back shortly with your custom plan."}
              </div>
            ) : (
              <>
                <div className="inline-flex rounded-full border border-slate-700 p-1 mb-4">
                  <button
                    type="button"
                    className={`px-4 py-1.5 rounded-full text-sm ${contactMode === 'email' ? 'bg-teal-600 text-white' : 'text-slate-300'}`}
                    onClick={() => { setContactMode('email'); setContactValue(''); }}
                  >
                    <Mail className="w-3.5 h-3.5 inline mr-1.5" /> Email
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-1.5 rounded-full text-sm ${contactMode === 'phone' ? 'bg-teal-600 text-white' : 'text-slate-300'}`}
                    onClick={() => { setContactMode('phone'); setContactValue(''); }}
                  >
                    <Phone className="w-3.5 h-3.5 inline mr-1.5" /> Phone
                  </button>
                </div>
                <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type={contactMode === 'email' ? 'email' : 'tel'}
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                    placeholder={contactMode === 'email' ? 'you@yourtrade.com.au' : '0412 345 678'}
                    className="bg-white/10 border-slate-700 text-white placeholder:text-slate-400"
                    required
                  />
                  <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700 shrink-0">
                    {submitting ? 'Sending...' : contactMode === 'email' ? 'Send my audit' : 'Call me back'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
                <p className="text-xs text-slate-400 mt-3">No spam. One follow-up only.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

